import { CircleDollarSign } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { opcoesDeCompetencia } from '@/componentes/competencias';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { totalSobreTudo } from '@/componentes/totais';
import { HistoriaNaMesa } from '@/historias/mesa';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarCompetencia, formatarDataHora, formatarInteiro, formatarReais, interpretarNumero } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import {
  explicarFalha,
  useCadastros,
  useCobrancas,
  useConciliarCobranca,
  useExternosSemCobranca,
  useLancarCobranca,
} from './consultas';
import { comErroDe, conferirTodos, type ErrosDeCampo, marcaDeErro, NENHUM_ERRO, temErro } from './erros-de-campo';
import type { Abastecimento, Cobranca } from './tipos';
import { totalDoAbastecimento } from './tipos';
import { HubDoCombustivel } from './HubDoCombustivel';
import { AcoesNoRodape, JanelaDeAcaoComRodape } from './RodapeDaJanela';
import { litrosEscritos } from './litros';

/**
 * Postos e conciliação de cobrança — a segunda metade da ferramenta 3.
 *
 * ## A pergunta
 *
 * "O posto está cobrando o que a gente abasteceu?" De um lado, a fatura como
 * ela chegou; do outro, a soma dos abastecimentos externos que foram ligados a
 * ela. A diferença fica na terceira coluna.
 *
 * ## Quem decide se a cobrança está conciliada é a conta, não a pessoa
 *
 * Não há campo "situação" nesta tela. Ligar os abastecimentos à fatura é o ato;
 * a situação (`conciliada` ou `divergente`) é consequência, e quem a escreve é o
 * servidor, comparando cobrado com lançado. Deixar alguém marcar "conciliada"
 * com 600 reais de diferença transformaria a conferência em formalidade — e o
 * risco que o documento v6 nomeia é exatamente "pagamento de abastecimentos não
 * conciliados".
 *
 * ## Abastecimento sem preço não soma
 *
 * A view não inventa valor: lançamento externo sem preço entra como nulo e fica
 * de fora do total. A tela diz isso na linha, porque é ele que faz a diferença
 * parecer maior do que é.
 */

const COLUNAS: readonly Coluna<Cobranca>[] = [
  {
    chave: 'documento',
    rotulo: 'Documento',
    celula: (c) => (
      <>
        <span className="codigo block text-tinta">{c.documento}</span>
        <TextoDaCelula texto={c.posto ?? c.fornecedor} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </>
    ),
  },
  {
    chave: 'competencia',
    rotulo: 'Competência',
    /* O mês como o manual escreve data (p.12): "09/2026", e não o
       "2026-09-01" que o banco guarda. Data não quebra. */
    quebra: 'nunca',
    celula: (c) => (c.competencia === null ? 'Sem competência' : formatarCompetencia(c.competencia)),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    /* Rótulo escrito, nunca só a cor. E sem badge: cor de status pertence à
       máquina (p.09), não à fatura. */
    celula: (c) => <span className="txt-corpo text-tinta">{situacaoEscrita(c.situacao)}</span>,
  },
  {
    chave: 'cobrado',
    rotulo: 'Cobrado',
    alinhamento: 'numero',
    celula: (c) => <span className="text-tinta">{formatarReais(c.valorCobrado)}</span>,
    total: (linhas) => (
      <span className="text-tinta">{formatarReais(linhas.reduce((s, c) => s + c.valorCobrado, 0))}</span>
    ),
  },
  {
    chave: 'lancado',
    rotulo: 'Lançado',
    alinhamento: 'numero',
    celula: (c) => (
      <>
        <span className="text-tinta">{formatarReais(c.valorLancado)}</span>
        <span className="txt-linha2 block text-tinta-secundaria">
          {`${formatarInteiro(c.abastecimentos)} abastecimentos · ${litrosEscritos(c.litros)}\u00a0L`}
        </span>
      </>
    ),
    total: (linhas) => (
      <span className="text-tinta">{formatarReais(linhas.reduce((s, c) => s + c.valorLancado, 0))}</span>
    ),
  },
  {
    chave: 'diferenca',
    rotulo: 'Diferença',
    alinhamento: 'numero',
    /* Diferença diferente de zero é a divergência — o próprio número é o
       alerta, e sai em vermelho (R02, p.31; regra 14). A linha de baixo diz o
       que fazer: cor nunca sozinha. */
    celula: (c) => (
      <>
        <span className={c.diferenca === 0 ? 'text-tinta' : 'text-parada'}>{formatarReais(c.diferenca)}</span>
        <span className="txt-linha2 block text-tinta-secundaria">
          {c.diferenca === 0 ? 'fecha com o lançado' : 'confira antes de pagar'}
        </span>
      </>
    ),
  },
];

function situacaoEscrita(situacao: Cobranca['situacao']): string {
  if (situacao === 'conciliada') return 'Conciliada';
  if (situacao === 'divergente') return 'Divergente';
  return 'Aberta';
}

/**
 * As colunas dos externos soltos. A PRIMEIRA é a identidade da linha e fica
 * fixa ao rolar de lado (p.18): o equipamento, com a caixa de marcar dentro
 * dela. Com a caixa numa coluna própria à frente, o que ficava fixo em 390 era
 * a caixa, e o equipamento sumia na rolagem.
 */
function colunasDosExternos(
  marcados: readonly string[],
  semCobranca: boolean,
  alternar: (id: string) => void,
  /** `cobranca.conciliar`: sem ela, a lista é só leitura — sem a caixa de marcar. */
  podeMarcar: boolean,
): readonly Coluna<Abastecimento>[] {
  return [
    {
      chave: 'equipamento',
      rotulo: 'Equipamento',
      celula: (a) => (
        <label className="flex items-center gap-3">
          {podeMarcar ? (
            <input
              type="checkbox"
              className="size-4 shrink-0"
              checked={marcados.includes(a.id)}
              disabled={semCobranca}
              aria-label={`Ligar o abastecimento de ${a.equipamentoCodigo} à cobrança`}
              onChange={() => {
                alternar(a.id);
              }}
            />
          ) : null}
          <span className="flex min-w-0 flex-col">
            <span className="codigo text-tinta">{a.equipamentoCodigo}</span>
            <TextoDaCelula texto={a.posto ?? 'Sem posto'} classe="txt-apoio text-tinta-apagada" linhas={1} />
          </span>
        </label>
      ),
    },
    ...COLUNAS_EXTERNOS,
  ];
}

const COLUNAS_EXTERNOS: readonly Coluna<Abastecimento>[] = [
  { chave: 'quando', rotulo: 'Quando', quebra: 'nunca', celula: (a) => formatarDataHora(a.quando) },
  {
    chave: 'comprovante',
    rotulo: 'Comprovante',
    celula: (a) => a.comprovante ?? 'Sem comprovante',
  },
  {
    chave: 'litros',
    rotulo: 'Litros',
    alinhamento: 'numero',
    celula: (a) => <Medida valor={litrosEscritos(a.litros)} unidade="L" />,
  },
  {
    chave: 'valor',
    rotulo: 'Valor',
    alinhamento: 'numero',
    celula: (a) => {
      const total = totalDoAbastecimento(a);
      return total === null ? (
        <span className="numero text-tinta-secundaria">Sem preço</span>
      ) : (
        <span className="text-tinta">{formatarReais(total)}</span>
      );
    },
  },
];

export function PaginaConciliacao() {
  const [lancando, setLancando] = useState(false);
  const consulta = useCobrancas();
  const externos = useExternosSemCobranca();
  const cadastros = useCadastros();
  const conciliar = useConciliarCobranca();
  /* Lançar e conciliar cobrança: só com `cobranca.conciliar` (prova do marco,
     07/10/2026). Quem só lê o combustível vê as cobranças, sem os botões. */
  const sessao = useSessao();
  const podeConciliar = (sessao.data?.permissoes ?? []).includes('cobranca.conciliar');

  const cobrancas = consulta.data ?? [];
  const paginaDasCobrancas = usePaginacao(cobrancas);
  const soltos = externos.data?.linhas ?? [];
  const paginaDosExternos = usePaginacao(soltos);

  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<readonly string[]>([]);

  const cobranca = cobrancas.find((c) => c.id === escolhida) ?? null;
  const aPagar = cobrancas.reduce((soma, c) => soma + c.valorCobrado, 0);


  function alternar(id: string) {
    setMarcados((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));
  }

  return (
    <HubDoCombustivel>
      {/* Só o que a faixa do hub não diz (p.17). "Divergentes" saiu: era o
          "Cobranças divergentes" do hub, o mesmo número com outro rótulo.
          Cobrança não é status: tom da marca (R02, p.31). */}
      <FaixaIndicadores>
        <CardIndicador
          rotulo="Cobranças"
          icone={CircleDollarSign}
          tom="marca"
          carregando={consulta.isPending}
          valor={consulta.isPending ? '' : formatarInteiro(cobrancas.length)}
          contexto="lançadas no período carregado"
        />
        <CardIndicador
          rotulo="Valor cobrado"
          icone={CircleDollarSign}
          tom="marca"
          carregando={consulta.isPending}
          valor={consulta.isPending ? '' : formatarReais(aPagar)}
          contexto="soma das faturas listadas"
        />
      </FaixaIndicadores>

      {/* "Lançar cobrança" abre em JANELA (pedido do dono, 25/09): o formulário
          fixo ao lado da tabela tinha o próprio laranja, e o laranja da tela
          é o "Lançar abastecimento" do hub. A ação fica na linha de ações
          ACIMA da tabela que ela alimenta (p.15), e não entre as duas tabelas. */}
      {podeConciliar ? (
      <div className="flex justify-end">
        <HistoriaNaMesa codigo="BTN-CMB-09" className="flex">
          <JanelaDeAcaoComRodape
            rotulo="Lançar cobrança"
            titulo="Lançar cobrança"
            aberta={lancando}
            aoAbrir={() => {
              setLancando(true);
            }}
            aoFechar={() => {
              setLancando(false);
            }}
          >
            <FormularioDeCobranca
              aoCancelar={() => {
                setLancando(false);
              }}
              fornecedores={cadastros.data?.fornecedores ?? []}
              postos={cadastros.data?.postos ?? []}
              carregandoCadastros={cadastros.isPending}
            />
          </JanelaDeAcaoComRodape>
        </HistoriaNaMesa>
      </div>
      ) : null}

      <Tabela
        rotulo="Cobranças dos postos"
        colunas={totalSobreTudo(COLUNAS, cobrancas)}
        linhas={paginaDasCobrancas.visiveis}
        rodape={<RodapeTabela {...paginaDasCobrancas.rodape} />}
        alturaDupla
        chaveDaLinha={(c) => c.id}
        selecionada={cobranca?.id ?? null}
        aoSelecionar={(c) => {
          setEscolhida(c.id);
          setMarcados([]);
        }}
        carregando={consulta.isPending}
        rotuloDoTotal="Soma"
        erro={
          consulta.isError ? (
            <EstadoVazio
              titulo="Não foi possível carregar as cobranças"
              descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
              acao={
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    void consulta.refetch();
                  }}
                >
                  Carregar as cobranças
                </Botao>
              }
            />
          ) : null
        }
        vazio={
          <EstadoVazio
            titulo="Nenhuma cobrança lançada"
            descricao={
              podeConciliar
                ? 'Lance a primeira fatura do posto para começar a conferir o que é cobrado.'
                : 'Quem lança a fatura do posto é o perfil Controladoria. Ela aparece aqui depois de lançada.'
            }
            acao={
              podeConciliar ? (
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    setLancando(true);
                  }}
                >
                  Lançar cobrança
                </Botao>
              ) : undefined
            }
          />
        }
      />

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex flex-col gap-1 pb-4">
            <h2 className="txt-secao text-tinta">Abastecimentos externos sem cobrança</h2>
            <p className="txt-apoio text-tinta-secundaria">
              {!podeConciliar
                ? 'Quem liga os abastecimentos à cobrança é o perfil Controladoria.'
                : cobranca === null
                ? 'Escolha uma cobrança na tabela acima para ligar estes abastecimentos a ela.'
                : `Marque o que a cobrança ${cobranca.documento} está cobrando. A situação dela é recalculada pelo servidor.`}
            </p>
          </div>

          <Tabela
            rotulo="Abastecimentos externos sem cobrança"
            colunas={colunasDosExternos(marcados, cobranca === null, alternar, podeConciliar)}
            linhas={paginaDosExternos.visiveis}
            rodape={<RodapeTabela {...paginaDosExternos.rodape} />}
            alturaDupla
            chaveDaLinha={(a) => a.id}
            carregando={externos.isPending}
            erro={
              externos.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar os abastecimentos externos"
                  descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void externos.refetch();
                      }}
                    >
                      Carregar os abastecimentos
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              <EstadoVazio
                titulo="Nenhum abastecimento externo solto"
                descricao="Todo abastecimento de posto já está ligado a uma cobrança, ou ainda não há nenhum lançado."
              />
            }
          />

          {/* A recusa da conciliação sai no toast de falha da camada de
              chamadas (p.30, p.31), e não numa faixa no fluxo da página. */}
          {podeConciliar ? (
          <div className="flex items-center justify-end gap-2 pt-4">
            {/* Secundário desde o hub (25/09): o laranja da tela é o "Lançar
                abastecimento" do topo, e a regra é uma ação laranja por tela. */}
            <Botao
              nivel="secundario"
              disabled={cobranca === null || marcados.length === 0 || conciliar.isPending}
              onClick={() => {
                if (cobranca === null) return;
                conciliar.mutate(
                  {
                    id: cobranca.id,
                    abastecimentos: marcados,
                    contexto: { alvo: { codigo: cobranca.documento, nome: cobranca.fornecedor } },
                  },
                  {
                    onSuccess: () => {
                      setMarcados([]);
                    },
                  },
                );
              }}
            >
              Conciliar cobrança
            </Botao>
          </div>
          ) : null}
        </div>

      </div>
    </HubDoCombustivel>
  );
}

function FormularioDeCobranca({
  fornecedores,
  postos,
  carregandoCadastros,
  aoCancelar,
}: {
  readonly aoCancelar: () => void;
  readonly fornecedores: readonly { readonly id: string; readonly nome: string }[];
  readonly postos: readonly { readonly id: string; readonly nome: string }[];
  /** As listas ainda não chegaram: os controles são esqueleto, e não listas vazias (p.32). */
  readonly carregandoCadastros: boolean;
}) {
  const base = useId();
  const lancar = useLancarCobranca();

  const [fornecedor, setFornecedor] = useState('');
  const [posto, setPosto] = useState('');
  const [documento, setDocumento] = useState('');
  const [valor, setValor] = useState('');
  const [competencia, setCompetencia] = useState('');
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [pronto, setPronto] = useState<string | null>(null);

  /* A mesma regra no `onBlur` e no envio (p.16): o erro aparece debaixo do
     campo ao sair dele, e o envio mostra todos de uma vez. */
  function mensagemDe(campo: string): string | null {
    if (campo === 'fornecedor') return fornecedor ? null : 'Escolha o fornecedor que emitiu a fatura.';
    if (campo === 'documento') return documento.trim() ? null : 'Informe o número do documento da fatura.';
    if (campo === 'valor') {
      const informado = interpretarNumero(valor);
      return informado === null || informado < 0 ? 'Informe o valor total cobrado na fatura.' : null;
    }
    return null;
  }

  function conferir(campo: string) {
    setErros((atual) => comErroDe(atual, campo, mensagemDe(campo)));
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setPronto(null);

    const total = interpretarNumero(valor);
    const conferidos = conferirTodos(['fornecedor', 'documento', 'valor'], mensagemDe);
    setErros(conferidos);
    if (temErro(conferidos) || total === null) return;

    lancar.mutate(
      {
        fornecedorId: fornecedor,
        documento: documento.trim(),
        valorTotal: total,
        ...(posto ? { postoId: posto } : {}),
        /* A competência é um mês, e o banco guarda data: o dia 1 é a convenção
           e está escrita aqui em vez de escondida numa conversão. */
        ...(competencia ? { competencia: `${competencia}-01` } : {}),
        motivo: 'lançamento de cobrança pela tela',
        contexto: { alvo: { nome: fornecedores.find((f) => f.id === fornecedor)?.nome ?? '' } },
      },
      {
        /* Gravou, fecha (pedido do dono, 03/10/2026, no combustível): o aviso
           de sucesso da camada de chamadas diz o que entrou, e a janela
           aberta convidava a lançar de novo o que já foi lançado. */
        onSuccess: () => {
          aoCancelar();
        },
      },
    );
  }

  const falha = lancar.isError ? explicarFalha(lancar.error) : null;

  return (
    <form id={`${base}-formulario`} onSubmit={enviar} noValidate className="formulario-grade">
      {/* A recusa no TOPO do formulário (p.22). */}
      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {falha.erro}. {falha.comoResolver}
        </p>
      ) : null}
      <p className="campo-linha txt-apoio text-tinta-secundaria">
        A fatura como ela chegou. O que ela cobra só é conferido depois, ligando os abastecimentos a ela.
      </p>

      <div className="campo-largo min-w-0">
        <Campo id={`${base}-fornecedor`} rotulo="Fornecedor" erro={erros.fornecedor ?? null}>
          {carregandoCadastros ? (
            <ControleCarregando oQue="Carregando os fornecedores" />
          ) : (
            /* O seletor do sistema (p.23, p.31): busca a partir de oito, e
               com um fornecedor só ele já vem escolhido. */
            <ListaDeEscolha
              id={`${base}-fornecedor`}
              rotulo="Fornecedor"
              valor={fornecedor}
              aoMudar={(valor) => {
                setFornecedor(valor);
                setErros((atual) => comErroDe(atual, 'fornecedor', null));
              }}
              aoSair={() => {
                conferir('fornecedor');
              }}
              rotuloVazio="Escolha o fornecedor"
              opcoes={fornecedores.map((f) => ({ valor: f.id, rotulo: f.nome }))}
              idDoErro={erros.fornecedor ? `${base}-fornecedor-erro` : undefined}
            />
          )}
        </Campo>
      </div>

      <div className="campo-largo min-w-0">
        <Campo id={`${base}-posto`} rotulo="Posto" opcional ajuda="Uma fatura pode cobrir mais de um posto do mesmo fornecedor.">
          {carregandoCadastros ? (
            <ControleCarregando oQue="Carregando os postos" />
          ) : (
            <ListaDeEscolha
              id={`${base}-posto`}
              rotulo="Posto"
              valor={posto}
              aoMudar={setPosto}
              rotuloVazio="Sem posto específico"
              opcoes={postos.map((p) => ({ valor: p.id, rotulo: p.nome }))}
            />
          )}
        </Campo>
      </div>

      <Campo id={`${base}-documento`} rotulo="Documento" erro={erros.documento ?? null}>
        <input
          id={`${base}-documento`}
          type="text"
          autoComplete="off"
          value={documento}
          placeholder="NF 12281"
          className="controle w-full"
          onChange={(evento) => {
            setDocumento(evento.target.value);
            setErros((atual) => comErroDe(atual, 'documento', null));
          }}
          onBlur={() => {
            conferir('documento');
          }}
          {...marcaDeErro(erros, 'documento', `${base}-documento`)}
        />
      </Campo>

      <Campo id={`${base}-valor`} rotulo="Valor cobrado" erro={erros.valor ?? null}>
        <div className="flex items-center gap-2">
          <span className="txt-corpo shrink-0 text-tinta-apagada">R$</span>
          <input
            id={`${base}-valor`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={valor}
            placeholder="1.228,00"
            className="controle w-full"
            onChange={(evento) => {
              setValor(evento.target.value);
              setErros((atual) => comErroDe(atual, 'valor', null));
            }}
            onBlur={() => {
              conferir('valor');
            }}
            {...marcaDeErro(erros, 'valor', `${base}-valor`)}
          />
        </div>
      </Campo>

      {/* Por nome ("Setembro de 2026"), e não `type="month"`: sem seletor de mês no navegador, ele vira caixa de texto aaaa-mm (achado dos robôs, 20/10/2026). */}
      <CampoSelecao
        rotulo="Competência"
        opcional
        valor={competencia ? `${competencia}-01` : ''}
        aoMudar={(v) => {
          setCompetencia(v.slice(0, 7));
        }}
        opcoes={opcoesDeCompetencia(new Date(), competencia ? `${competencia}-01` : '')}
        rotuloVazio="Sem competência"
      />

      {pronto ? (
        <p role="status" className="campo-linha txt-apoio rounded-controle border border-borda bg-rebaixada px-4 py-2 text-tinta">
          {pronto}
        </p>
      ) : null}

      {/* Dentro da janela, a ação dela é a laranja: a janela bloqueia a página
          atrás, e o laranja do hub não se alcança com ela aberta. "Cancelar"
          é terciário à esquerda (p.15), como em todas as janelas do módulo,
          no rodapé fixo da janela (R01, p.29). */}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao nivel="primario" type="submit" form={`${base}-formulario`} disabled={lancar.isPending}>
          Lançar cobrança
        </Botao>
      </AcoesNoRodape>
    </form>
  );
}
